import React from 'react';
import { motion } from 'framer-motion';
import { FaCarSide, FaStar, FaCheckCircle } from 'react-icons/fa';
import { FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100
    }
  }
};

export default function Hero() {
  return (
    <section
      id="home"
      className="relative flex min-h-screen items-center overflow-hidden bg-brand-green pb-32 pt-32"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-brand-green/80 to-black/30" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-[100%] bg-brand-navy opacity-15 blur-3xl" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mx-auto mt-12 max-w-4xl text-center"
        >
          <motion.div
            variants={itemVariants}
            className="mb-6 inline-flex items-center gap-1 rounded-full border border-brand-gold/30 bg-brand-navy/80 px-3 py-1.5 text-[0.65rem] font-semibold text-brand-gold backdrop-blur-sm min-[375px]:text-xs sm:gap-2 sm:px-4 sm:text-sm"
          >
            <SafeIcon icon={FaStar} className="shrink-0 text-brand-gold" />
            <span className="truncate">5.0 Google Rating based on verified reviews</span>
            <SafeIcon icon={FaStar} className="shrink-0 text-brand-gold" />
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="text-embroidery mb-6 font-display text-5xl leading-tight text-white md:text-7xl lg:text-8xl"
          >
            Your Journey to <span className="text-brand-gold">Confident</span>{' '}
            Driving Starts Here
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="mx-auto mb-10 max-w-2xl text-xl font-medium text-white/90 md:text-2xl"
          >
            Calm, supportive automatic driving instruction and focused pre-test
            preparation in Goulburn, NSW.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="mb-12 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto"
            >
              <Link
                to="/booking"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-gold px-8 py-4 font-display text-xl text-brand-navy shadow-[0_8px_0_#1C2B5E] transition-all hover:translate-y-1 hover:bg-[#D4891A] hover:shadow-[0_4px_0_#1C2B5E]"
              >
                Book a Session
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </motion.div>

            <motion.a
              href="#instructors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex w-full items-center justify-center rounded-full border-2 border-white bg-white/10 px-8 py-3.5 font-display text-xl text-white backdrop-blur-md transition-all hover:bg-white/20 sm:w-auto"
            >
              Meet Our Instructors
            </motion.a>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-3 text-xs font-semibold text-white/80 min-[375px]:text-sm sm:gap-4 md:gap-8 md:text-base"
          >
            {[
              'New Learners Welcome',
              'Test Preparation',
              'Automatic Instruction'
            ].map((badge) => (
              <div key={badge} className="flex items-center gap-1 sm:gap-2">
                <SafeIcon
                  icon={FaCheckCircle}
                  className="shrink-0 text-brand-gold"
                />
                {badge}
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute bottom-0 left-0 h-32 w-full overflow-hidden">
        <div className="absolute bottom-0 h-16 w-full border-t-4 border-brand-gold/50 bg-brand-navy" />
        <svg width="100%" height="16" className="absolute bottom-6">
          <line
            x1="0"
            y1="8"
            x2="100%"
            y2="8"
            stroke="white"
            strokeWidth="4"
            strokeDasharray="30 40"
            className="animate-dash"
          />
        </svg>
        <div className="animate-drive absolute bottom-8 select-none">
          <SafeIcon
            icon={FaCarSide}
            className="h-16 w-16 text-brand-gold drop-shadow-lg"
          />
        </div>
      </div>
    </section>
  );
}